import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker';
import { TranslateModule } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { RouteReusableStrategy, ApiPrefixInterceptor, FrozenSessionInterceptor, SharedModule } from '@shared';
import { AuthModule } from '@app/auth';
import { ShellModule } from './shell/shell.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AdminModule } from './areas/admin/admin.module';
import { DashboardModule } from './areas/dashboard/dashboard.module';
import { MembersModule } from './areas/members/members.module';
import { LoyaltyDashboardModule } from './areas/loyalty-dashboard/loyalty-dashboard.module';
import { NonMembersModule } from './areas/non-members/non-members.module';
import { OffersModule } from './areas/offers/offers.module';
import { ChangePasswordModule } from './areas/change-password/change-password.module';
import { ToastModule } from 'primeng/toast';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReportsModule } from './areas/reports/reports.module';
import { ReviewsModule } from './areas/reviews/reviews.module';
import { TransactionsModule } from './areas/transactions/transactions.module';
import { NotFoundModule } from './areas/not-found/not-found.module';
import { EnrollModule } from './enroll/enroll.module';
import { MerchantRegistrationModule } from './merchant-registration/merchant-registration.module';
import '@shared/extensions/date.extensions';
import assetCacheBusting from '../../asset-cache-busting.json';
import { SignalRService } from '@app/resources/services/signalr.service';
import { getEnvironment } from '@shared/utils';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers, reducers } from '@app/resources/ngrx/reducers';
import { effects } from '@app/resources/ngrx/effects';
import { config } from '@app/resources/ngrx/dev-tools/store-devtools.config';
import { CustomSerializer } from '@app/resources/ngrx/states/router.state';
import { ConfigurationService, ERROR_METADATA_PROVIDER, ErrorQueueService, provideErrorReporting } from '@ep/shared';
import { selectApplicationProfile } from '@app/resources/ngrx/selectors';
import { map, take } from 'rxjs/operators';
import { environment } from '../environments/environment';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('./ngsw-worker.js', { enabled: false }),
    FormsModule,
    RouterModule,
    TranslateModule.forRoot(),
    NgbModule,
    SharedModule,
    ShellModule,
    AuthModule,
    AdminModule,
    DashboardModule,
    LoyaltyDashboardModule,
    MembersModule,
    NonMembersModule,
    OffersModule,
    ReportsModule,
    ReviewsModule,
    TransactionsModule,
    ChangePasswordModule,
    NotFoundModule,
    EnrollModule,
    MerchantRegistrationModule,
    ToastModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot({ serializer: CustomSerializer }),
    StoreDevtoolsModule.instrument(config),
    AppRoutingModule, // must be imported as the last module as it contains the fallback route
  ],
  providers: [
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (configurationService: ConfigurationService, injector: Injector) => async () => {
        await configurationService.load(
          getEnvironment(),
          assetCacheBusting[getEnvironment() as keyof typeof assetCacheBusting]
        );
        injector.get(ErrorQueueService).flush();
      },
      deps: [ConfigurationService, Injector],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FrozenSessionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy,
    },
    ...provideErrorReporting({ sourceName: 'EP.MerchantPortal', apiKey: environment.serviceApiKey }),
    {
      provide: ERROR_METADATA_PROVIDER,
      useFactory: (store: Store) => () =>
        store.select(selectApplicationProfile).pipe(
          take(1),
          map((profile) =>
            !profile
              ? {}
              : {
                  merchantId: String(profile.Merchant?.MerchantId ?? ''),
                  merchantName: profile.Merchant?.Name ?? '',
                  locationId: String(profile.Location?.LocationId ?? ''),
                  locationName: profile.Location?.Name ?? '',
                  employeeId: String(profile.Employee?.EmployeeId ?? ''),
                  employeeName: `${profile.Employee?.NameFirst ?? ''} ${profile.Employee?.NameLast ?? ''}`.trim(),
                }
          )
        ),
      deps: [Store],
    },
    SignalRService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(configurationService: ConfigurationService, private swUpdate: SwUpdate) {
    if (configurationService.isProduction) {
      this.swUpdate.activateUpdate().then(() => {});
    }
  }
}
